import type {
  V2BookTriggerDescriptor,
  V2BookPollDescriptor,
  Bdkv2ConceptDescriptor,
  Bdkv2ConceptType,
  V2BookTriggerDescriptorConfiguration,
} from '@/generated/hooks/bdk/models';
import type {
  ITriggerFieldOption,
  ITriggerFieldGroup,
  IGetPrimaryButtonLabelParams,
  IGetPrimaryButtonVariantParams,
  IGetDialogTitleParams,
  IOptionWithGroup,
  TConfigurationValues,
} from './types';

/**
 * Extracts trigger field options from a trigger's event_type as grouped options.
 *
 * Scans all keys in the event_type map and for each key:
 * - If dictionary_type exists, creates a group with eventKey as group label
 *   and dictionary fields as options within the group
 * - If not dictionary_type, creates an ungrouped entry (groupLabel: null) with eventKey
 *   as the single option (no group wrapper in UI)
 *
 * Works for any descriptor that exposes an `event_type` map, so it is shared by
 * both webhook/email trigger descriptors and poll descriptors.
 *
 * @param trigger - The selected trigger or poll descriptor
 * @returns Array of trigger field groups with grouped options
 */
export function extractTriggerFieldGroups(
  trigger?: V2BookTriggerDescriptor | V2BookPollDescriptor,
): ITriggerFieldGroup[] {
  if (!trigger?.event_type) {
    return [];
  }

  const groups: ITriggerFieldGroup[] = [];

  // Scan all keys in the event_type map
  Object.entries(trigger.event_type).forEach(
    ([eventKey, conceptDescriptor]) => {
      const conceptType = conceptDescriptor?.type;

      // Check if dictionary_type exists - these are real groups
      if (conceptType?.dictionary_type?.fields) {
        const fields = conceptType.dictionary_type.fields;
        const options: ITriggerFieldOption[] = [];

        // Extract key from each field
        fields.forEach(field => {
          if (field.key) {
            options.push({
              label: field.key,
              value: `${eventKey}.${field.key}`,
            });
          }
        });

        if (options.length > 0) {
          groups.push({
            groupLabel: eventKey,
            options,
          });
        }
      } else {
        // Non-dictionary type: use eventKey itself as a single option (not grouped)
        groups.push({
          groupLabel: null,
          options: [
            {
              label: eventKey,
              value: eventKey,
            },
          ],
        });
      }
    },
  );

  return groups;
}

/**
 * Flattens trigger field groups into a flat array of options.
 * Useful for finding options by value or for backwards compatibility.
 *
 * @param groups - Array of trigger field groups
 * @returns Flat array of trigger field options
 */
export function flattenTriggerFieldGroups(
  groups: ITriggerFieldGroup[],
): ITriggerFieldOption[] {
  return groups.flatMap(group => group.options);
}

/**
 * Gets the dynamic primary button label based on mode, step, and changes.
 *
 * @param params - Parameters for determining the label
 * @returns The appropriate button label
 */
export function getPrimaryButtonLabel({
  mode,
  isLastStep,
  hasAnyChanges,
  hasConfigureChanges,
  hasInputSpecs = true,
  isLoading = false,
  translations,
}: IGetPrimaryButtonLabelParams): string {
  const isEditMode = mode === 'edit';

  if (isLoading) {
    return isEditMode
      ? (translations.updating ?? translations.updateTrigger)
      : (translations.saving ?? translations.saveTrigger);
  }

  // If no inputSpecs, we're always on step 1 (single step)
  if (!hasInputSpecs) {
    if (isEditMode) {
      return hasAnyChanges ? translations.updateTrigger : translations.done;
    }
    // Create mode with no inputSpecs
    return translations.saveTrigger;
  }

  // Multi-step flow (has inputSpecs)
  if (isEditMode) {
    if (isLastStep) {
      return hasAnyChanges ? translations.updateTrigger : translations.done;
    }
    // First step in edit mode
    return hasConfigureChanges ? translations.saveAndNext : translations.next;
  }

  // Create mode
  return isLastStep ? translations.saveTrigger : translations.saveAndNext;
}

/**
 * Gets the dynamic primary button variant based on mode, step, and changes.
 * In edit mode: secondary for "Next" and "Done" (no changes), default for others.
 *
 * @param params - Parameters for determining the variant
 * @returns The button variant ('default' or 'secondary')
 */
export function getPrimaryButtonVariant({
  mode,
  isLastStep,
  hasAnyChanges,
  hasConfigureChanges,
}: IGetPrimaryButtonVariantParams): 'default' | 'secondary' {
  const isEditMode = mode === 'edit';

  if (isEditMode) {
    if (isLastStep) {
      // "Done" (no changes) → secondary, "Update Trigger" (changes) → default
      return hasAnyChanges ? 'default' : 'secondary';
    }
    // First step: "Next" (no changes) → secondary, "Save & Next" (changes) → default
    return hasConfigureChanges ? 'default' : 'secondary';
  }

  // Create mode - always default (primary)
  return 'default';
}

/**
 * Gets the dialog title based on mode.
 *
 * @param params - Parameters for determining the title
 * @returns The appropriate dialog title
 */
export function getDialogTitle({
  mode,
  triggerName,
  translations,
}: IGetDialogTitleParams): string {
  if (mode === 'readOnly' && triggerName) {
    return translations.viewTitle;
  }
  if (mode === 'edit' && triggerName) {
    return translations.editTitle;
  }
  return translations.createTitle;
}

/**
 * Gets the default value from a concept descriptor.
 *
 * Extracts the default value from different value types:
 * - text_value
 * - number_value
 * - boolean_value
 * - sensitive_value (nested text_value)
 *
 * @param descriptor - The concept descriptor containing the default value
 * @returns The default value if present, undefined otherwise
 */
export function getDefaultValue(
  descriptor: Bdkv2ConceptDescriptor,
): string | boolean | number | undefined {
  const defaultValue = descriptor.default_value;
  if (!defaultValue) return undefined;

  if (defaultValue.text_value !== undefined) return defaultValue.text_value;
  if (defaultValue.number_value !== undefined) return defaultValue.number_value;
  if (defaultValue.boolean_value !== undefined)
    return defaultValue.boolean_value;
  if (defaultValue.sensitive_value?.value?.text_value !== undefined)
    return defaultValue.sensitive_value.value.text_value;

  return undefined;
}

/**
 * Gets the trigger field option with its group info by value, searching across all groups.
 *
 * @param triggerFieldGroups - Array of trigger field groups to search
 * @param value - The value to find
 * @returns The option with its group info, or undefined if not found
 */
export function getOptionWithGroupByValue(
  triggerFieldGroups: ITriggerFieldGroup[],
  value: string | undefined,
): IOptionWithGroup | undefined {
  if (!value) return undefined;

  for (const group of triggerFieldGroups) {
    const option = group.options.find(opt => opt.value === value);
    if (option) {
      return { option, groupLabel: group.groupLabel };
    }
  }

  return undefined;
}

/**
 * Returns `true` if the descriptor's type is wrapped in `optional_type`.
 *
 * The BDK schema uses `optional_type` as a sibling of `scalar_type` / `enum_type`
 * etc., so optional fields look like `{ optional_type: { inner: { scalar_type: ... } } }`.
 * This helper hides that detail from callers.
 */
export function isOptionalField(descriptor: Bdkv2ConceptDescriptor): boolean {
  return !!descriptor.type?.optional_type;
}

/**
 * Returns the effective `ConceptType` for input rendering.
 *
 * For optional fields the actual scalar/enum/etc. lives one level deeper inside
 * `optional_type.inner`. Reading `descriptor.type` directly would miss it and
 * fall through to a default text input — that's the bug this helper fixes.
 *
 * For non-optional descriptors this returns the type unchanged.
 */
export function unwrapInnerType(
  descriptor: Bdkv2ConceptDescriptor,
): Bdkv2ConceptType | undefined {
  if (descriptor.type?.optional_type?.inner) {
    return descriptor.type.optional_type.inner;
  }
  return descriptor.type;
}

/**
 * Returns the keys of `configuration` whose descriptor is required (not wrapped
 * in `optional_type`) and whose current value in `values` is missing.
 *
 * "Missing" means:
 * - the key is absent from `values`
 * - the value is `undefined` or `null`
 * - the value is an empty string (after trimming)
 *
 * Booleans and numbers are never considered missing if they appear in `values`;
 * `false` / `0` are valid user-entered choices.
 *
 * Returns an empty array when nothing is missing, which makes call sites read
 * naturally as `if (missing.length > 0) { ... }`.
 */
export function getMissingRequiredConfigFields(
  configuration: V2BookTriggerDescriptorConfiguration | undefined,
  values: TConfigurationValues | undefined,
): string[] {
  if (!configuration) return [];

  const missing: string[] = [];
  for (const [key, descriptor] of Object.entries(configuration)) {
    if (isOptionalField(descriptor)) continue;

    const value = values?.[key];
    if (value === undefined || value === null) {
      missing.push(key);
      continue;
    }
    if (typeof value === 'string' && value.trim().length === 0) {
      missing.push(key);
    }
  }
  return missing;
}
